import { useState, useEffect, useMemo, useCallback } from 'react';
import type { ComponentType } from 'react';
import { Plus, Edit2, Trash2, Clock, XCircle, Filter, Play, Pause, Square } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { DetailField } from '@/components/common/DetailSection';
import { FormField } from '@/components/common/FormSection';
import { AmountText, FinanceStatusBadge } from '@/components/finance-v2/FinanceBadges';
import { financeV2Service } from '../services/financeV2Service';
import { Timesheet, ServiceType, ActiveTimer } from '../types/financeV2.types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { clientService } from '../services/client-service';
import { parseDbTimestamp } from '../utils/datetime';
import { cn } from '@/utils/cn';
import { NativeSelect } from "@/components/ui/native-select"
import { DataTable } from "@/components/ui/data-table"

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendente',
  INVOICED: 'Faturado',
  CANCELLED: 'Cancelado',
};

const EMPTY_TIMER_FORM = {
  service_type_id: '',
  client_id: '',
  case_id: '',
  description: ''
};

/** Botão de ação da linha: só ícone, com nome acessível e dica. */
function RowAction({
  label,
  icon: Icon,
  onClick,
  className,
}: {
  label: string;
  icon: ComponentType<{ className?: string }>;
  onClick: () => void;
  className?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="ghost" size="icon-sm" onClick={onClick} aria-label={label} className={className}>
          <Icon className="size-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

export function FinanceV2TimesheetsPage() {
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  type ClientLite = { id: string; personalData?: { fullName?: string }; companyData?: { companyName?: string } }
  const [clients, setClients] = useState<ClientLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showTimerModal, setShowTimerModal] = useState(false);
  const [editingTimesheet, setEditingTimesheet] = useState<Timesheet | null>(null);
  const [activeTimer, setActiveTimer] = useState<ActiveTimer | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [filters, setFilters] = useState<{
    status: '' | 'PENDING' | 'INVOICED' | 'CANCELLED';
    service_type_id: string;
    from_date: string;
    to_date: string;
  }>({
    status: '',
    service_type_id: '',
    from_date: '',
    to_date: ''
  });

  const [timerFormData, setTimerFormData] = useState(EMPTY_TIMER_FORM);

  const getLocalDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [formData, setFormData] = useState({
    service_type_id: '',
    client_id: '',
    case_id: '',
    work_date: getLocalDate(),
    duration_minutes: '',
    description: '',
    notes: ''
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [timesheetsRes, serviceTypesRes, clientsRes] = await Promise.all([
        financeV2Service.timesheets.getAll({ ...filters, status: filters.status || undefined }),
        financeV2Service.serviceTypes.getAllActive(),
        clientService.getAllClients({ status: 'ACTIVE' }, { page: 1, limit: 1000, sort: 'createdAt:desc' })
      ]);

      if (timesheetsRes.success) {
        setTimesheets(timesheetsRes.data.timesheets);
      }
      if (serviceTypesRes.success) {
        setServiceTypes(serviceTypesRes.data.service_types);
      }
      // clientsRes is already a ClientListResponse object with a clients array
      setClients(clientsRes.clients);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load active timer on mount
  useEffect(() => {
    loadActiveTimer();
  }, []);

  // Update elapsed time every 30 seconds (display is in minutes anyway)
  useEffect(() => {
    if (!activeTimer || activeTimer.paused_at) return;

    const calcElapsed = () => {
      const startTime = parseDbTimestamp(activeTimer.start_time)?.getTime();
      if (!startTime) return 0;
      const pausedDuration = activeTimer.total_paused_duration || 0;
      return Math.floor((Date.now() - startTime - pausedDuration) / 1000 / 60);
    };

    setElapsedTime(calcElapsed());
    const interval = setInterval(() => setElapsedTime(calcElapsed()), 30000);

    return () => clearInterval(interval);
  }, [activeTimer]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const data = {
        service_type_id: formData.service_type_id,
        client_id: formData.client_id,
        case_id: formData.case_id || undefined,
        work_date: formData.work_date,
        duration_minutes: parseInt(formData.duration_minutes),
        description: formData.description,
        notes: formData.notes || undefined
      };

      if (editingTimesheet) {
        await financeV2Service.timesheets.update(editingTimesheet.id, data);
      } else {
        await financeV2Service.timesheets.create(data);
      }

      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error saving timesheet:', error);
      alert('Erro ao salvar registro de horas');
    }
  };

  const handleEdit = (timesheet: Timesheet) => {
    setEditingTimesheet(timesheet);
    setFormData({
      service_type_id: timesheet.service_type_id,
      client_id: timesheet.client_id,
      case_id: timesheet.case_id || '',
      work_date: timesheet.work_date,
      duration_minutes: timesheet.duration_minutes.toString(),
      description: timesheet.description,
      notes: timesheet.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = useCallback(async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este registro?')) {
      return;
    }

    try {
      await financeV2Service.timesheets.delete(id);
      loadData();
    } catch (error) {
      console.error('Error deleting timesheet:', error);
      alert('Erro ao excluir registro');
    }
  }, [loadData]);

  const handleCancel = useCallback(async (id: string) => {
    if (!confirm('Tem certeza que deseja cancelar este registro?')) {
      return;
    }

    try {
      await financeV2Service.timesheets.cancel(id);
      loadData();
    } catch (error) {
      console.error('Error cancelling timesheet:', error);
      alert('Erro ao cancelar registro');
    }
  }, [loadData]);

  const loadActiveTimer = async () => {
    try {
      const response = await financeV2Service.timer.getActive();
      if (response.success && response.data.timer) {
        setActiveTimer(response.data.timer);
        const timer = response.data.timer;
        const pausedDuration = timer.total_paused_duration || 0;
        const startTime = parseDbTimestamp(timer.start_time)?.getTime() ?? Date.now();
        setElapsedTime(Math.floor((Date.now() - startTime - pausedDuration) / 1000 / 60));
      }
    } catch (error) {
      console.error('Error loading active timer:', error);
    }
  };

  const handleStartTimer = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const data = {
        service_type_id: timerFormData.service_type_id,
        description: timerFormData.description,
        client_id: timerFormData.client_id || undefined,
        case_id: timerFormData.case_id || undefined
      };

      const response = await financeV2Service.timer.start(data);

      if (response.success) {
        setShowTimerModal(false);
        setTimerFormData(EMPTY_TIMER_FORM);
        setActiveTimer(response.data.timer);
        alert('Cronômetro iniciado com sucesso!');
      }
    } catch (error) {
      console.error('Error starting timer:', error);
      alert('Erro ao iniciar cronômetro');
    }
  };

  const handlePauseTimer = async () => {
    try {
      const response = await financeV2Service.timer.pause();
      if (response.success) {
        setActiveTimer(response.data.timer);
      }
    } catch (error) {
      console.error('Error pausing timer:', error);
      alert('Erro ao pausar cronômetro');
    }
  };

  const handleResumeTimer = async () => {
    try {
      const response = await financeV2Service.timer.resume();
      if (response.success) {
        setActiveTimer(response.data.timer);
      }
    } catch (error) {
      console.error('Error resuming timer:', error);
      alert('Erro ao retomar cronômetro');
    }
  };

  const handleStopTimer = async () => {
    if (!confirm('Deseja parar o cronômetro? O tempo será salvo como um registro de horas.')) {
      return;
    }

    try {
      const response = await financeV2Service.timer.stop();
      if (response.success) {
        setActiveTimer(null);
        setElapsedTime(0);
        loadData(); // Reload timesheets to show the new record
        alert('Cronômetro parado com sucesso!');
      }
    } catch (error) {
      console.error('Error stopping timer:', error);
      alert('Erro ao parar cronômetro');
    }
  };

  const resetForm = () => {
    setFormData({
      service_type_id: '',
      client_id: '',
      case_id: '',
      work_date: getLocalDate(),
      duration_minutes: '',
      description: '',
      notes: ''
    });
    setEditingTimesheet(null);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h${mins > 0 ? ` ${mins}min` : ''}`;
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "dd/MM/yyyy", { locale: ptBR });
  };

  const timesheetColumns = useMemo<ColumnDef<Timesheet>[]>(() => [
    {
      accessorKey: 'work_date',
      header: 'Data',
      cell: ({ row }) => (
        <span className="text-sm tabular-nums">{formatDate(row.original.work_date)}</span>
      ),
    },
    {
      accessorKey: 'service_type_name',
      header: 'Serviço',
      cell: ({ row }) => (
        <span className="text-sm">{row.original.service_type_name}</span>
      ),
    },
    {
      accessorKey: 'client_name',
      header: 'Cliente',
      cell: ({ row }) => (
        <span className="text-sm">{row.original.client_name}</span>
      ),
    },
    {
      accessorKey: 'description',
      header: 'Descrição',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="max-w-xs truncate text-sm text-muted-foreground">
          {row.original.description}
        </div>
      ),
    },
    {
      accessorKey: 'duration_minutes',
      header: 'Duração',
      cell: ({ row }) => (
        <span className="text-sm tabular-nums">{formatDuration(row.original.duration_minutes)}</span>
      ),
    },
    {
      accessorKey: 'calculated_value',
      header: 'Valor',
      cell: ({ row }) => (
        <AmountText tone="neutral" className="text-sm">
          {formatCurrency(row.original.calculated_value)}
        </AmountText>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <FinanceStatusBadge status={row.original.status}>
          {STATUS_LABELS[row.original.status] ?? row.original.status}
        </FinanceStatusBadge>
      ),
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">Ações</span>,
      enableSorting: false,
      cell: ({ row }) => {
        const timesheet = row.original;
        return (
          <div className="flex justify-end gap-1">
            {timesheet.status === 'PENDING' && (
              <>
                <RowAction label="Editar" icon={Edit2} onClick={() => handleEdit(timesheet)} />
                <RowAction label="Excluir" icon={Trash2} className="text-destructive" onClick={() => handleDelete(timesheet.id)} />
              </>
            )}
            {timesheet.status === 'INVOICED' && (
              <RowAction label="Cancelar" icon={XCircle} onClick={() => handleCancel(timesheet.id)} />
            )}
          </div>
        );
      },
    },
  ], [handleCancel, handleDelete]);

  const openTimerModal = () => {
    setTimerFormData(EMPTY_TIMER_FORM);
    setShowTimerModal(true);
  };

  const closeTimerModal = () => {
    setShowTimerModal(false);
    setTimerFormData(EMPTY_TIMER_FORM);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-4 p-4 sm:p-6" aria-busy="true">
        <span className="sr-only">Carregando...</span>
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-24" />
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14" />)}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            <Clock className="size-6 text-muted-foreground" />
            <h1 className="text-2xl font-semibold">Registro de Horas</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Gerencie seus registros de horas trabalhadas
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={openTimerModal}>
            <Play className="size-4" />
            Iniciar Cronômetro
          </Button>
          <Button onClick={openCreateModal}>
            <Plus className="size-4" />
            Novo Registro
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card size="sm">
        <CardContent className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Filter className="size-4 text-muted-foreground" aria-hidden="true" />
            <h3 className="text-sm font-medium">Filtros</h3>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <FormField label="Status" htmlFor="timesheets-filter-status">
              <NativeSelect
                id="timesheets-filter-status"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value as '' | 'PENDING' | 'INVOICED' | 'CANCELLED' })}
              >
                <option value="">Todos</option>
                <option value="PENDING">Pendente</option>
                <option value="INVOICED">Faturado</option>
                <option value="CANCELLED">Cancelado</option>
              </NativeSelect>
            </FormField>

            <FormField label="Tipo de Serviço" htmlFor="timesheets-filter-service-type">
              <NativeSelect
                id="timesheets-filter-service-type"
                value={filters.service_type_id}
                onChange={(e) => setFilters({ ...filters, service_type_id: e.target.value })}
              >
                <option value="">Todos</option>
                {serviceTypes.map((st) => (
                  <option key={st.id} value={st.id}>
                    {st.name}
                  </option>
                ))}
              </NativeSelect>
            </FormField>

            <FormField label="Data Inicial" htmlFor="timesheets-filter-from">
              <Input
                id="timesheets-filter-from"
                type="date"
                value={filters.from_date}
                onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
              />
            </FormField>

            <FormField label="Data Final" htmlFor="timesheets-filter-to">
              <Input
                id="timesheets-filter-to"
                type="date"
                value={filters.to_date}
                onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
              />
            </FormField>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      {timesheets.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <Clock className="size-10 text-muted-foreground" aria-hidden="true" />
            <h3 className="text-sm font-medium">Nenhum registro encontrado</h3>
            <p className="text-sm text-muted-foreground">Comece registrando suas horas de trabalho</p>
            <Button className="mt-2" onClick={openCreateModal}>
              <Plus className="size-4" />
              Criar Registro
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="min-w-0 py-0">
          <div className="min-w-0 overflow-x-auto p-2">
            <DataTable
              columns={timesheetColumns}
              data={timesheets}
              minWidth="min-w-[960px]"
              emptyMessage="Nenhum registro encontrado"
              pageSize={20}
            />
          </div>
        </Card>
      )}

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={(open) => { if (!open) closeModal() }}>
        <DialogContent size="lg" fullScreenOnMobile className="overflow-y-auto sm:max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{editingTimesheet ? 'Editar Registro' : 'Novo Registro de Horas'}</DialogTitle>
            <DialogDescription>Horas trabalhadas para um cliente, valoradas pelo tipo de serviço.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex min-w-0 flex-col gap-4">
            <FormField label="Cliente *" htmlFor="timesheet-client">
              <NativeSelect
                id="timesheet-client"
                required
                value={formData.client_id}
                onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
              >
                <option value="">Selecione...</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.personalData?.fullName || client.companyData?.companyName}
                  </option>
                ))}
              </NativeSelect>
            </FormField>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label="Tipo de Serviço *" htmlFor="timesheet-service-type">
                <NativeSelect
                  id="timesheet-service-type"
                  required
                  value={formData.service_type_id}
                  onChange={(e) => setFormData({ ...formData, service_type_id: e.target.value })}
                >
                  <option value="">Selecione...</option>
                  {serviceTypes.map((st) => (
                    <option key={st.id} value={st.id}>
                      {st.name} - {formatCurrency(st.hourly_rate)}/h
                    </option>
                  ))}
                </NativeSelect>
              </FormField>

              <FormField label="Data *" htmlFor="timesheet-date">
                <Input
                  id="timesheet-date"
                  type="date"
                  required
                  value={formData.work_date}
                  onChange={(e) => setFormData({ ...formData, work_date: e.target.value })}
                />
              </FormField>
            </div>

            <FormField
              label="Duração (minutos) *"
              htmlFor="timesheet-duration"
              hint={formData.duration_minutes ? `= ${formatDuration(parseInt(formData.duration_minutes))}` : undefined}
            >
              <Input
                id="timesheet-duration"
                type="number"
                required
                min="1"
                value={formData.duration_minutes}
                onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
                placeholder="Ex: 120 (2 horas)"
              />
            </FormField>

            <FormField label="Descrição *" htmlFor="timesheet-description">
              <Textarea
                id="timesheet-description"
                required
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                placeholder="Descreva o trabalho realizado"
              />
            </FormField>

            <FormField label="Observações" htmlFor="timesheet-notes">
              <Textarea
                id="timesheet-notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                placeholder="Observações adicionais (opcional)"
              />
            </FormField>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeModal}>
                Cancelar
              </Button>
              <Button type="submit">
                {editingTimesheet ? 'Salvar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Timer Modal */}
      <Dialog open={showTimerModal} onOpenChange={(open) => { if (!open) closeTimerModal() }}>
        <DialogContent size="sm" fullScreenOnMobile className="overflow-y-auto sm:max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="size-4 text-primary" aria-hidden="true" />
              Iniciar Cronômetro
            </DialogTitle>
            <DialogDescription>O tempo corre até você parar e vira um registro de horas.</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleStartTimer} className="flex min-w-0 flex-col gap-4">
            <FormField label="Cliente *" htmlFor="timer-client">
              <NativeSelect
                id="timer-client"
                required
                value={timerFormData.client_id}
                onChange={(e) => setTimerFormData({ ...timerFormData, client_id: e.target.value })}
              >
                <option value="">Selecione...</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.personalData?.fullName || client.companyData?.companyName}
                  </option>
                ))}
              </NativeSelect>
            </FormField>

            <FormField label="Tipo de Serviço *" htmlFor="timer-service-type">
              <NativeSelect
                id="timer-service-type"
                required
                value={timerFormData.service_type_id}
                onChange={(e) => setTimerFormData({ ...timerFormData, service_type_id: e.target.value })}
              >
                <option value="">Selecione...</option>
                {serviceTypes.map((st) => (
                  <option key={st.id} value={st.id}>
                    {st.name} - {formatCurrency(st.hourly_rate)}/h
                  </option>
                ))}
              </NativeSelect>
            </FormField>

            <FormField label="Descrição do Trabalho *" htmlFor="timer-description">
              <Textarea
                id="timer-description"
                required
                value={timerFormData.description}
                onChange={(e) => setTimerFormData({ ...timerFormData, description: e.target.value })}
                rows={3}
                placeholder="Descreva o trabalho que será realizado..."
              />
            </FormField>

            <div className="rounded-lg border border-border bg-muted/50 p-3 text-sm">
              <strong>Nota:</strong> O cronômetro aparecerá no canto inferior direito da tela.
              Você poderá pausar, retomar ou parar a qualquer momento.
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeTimerModal}>
                Cancelar
              </Button>
              <Button type="submit">
                <Play className="size-4" />
                Iniciar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Floating Timer Widget */}
      {activeTimer && (
        <div className="fixed bottom-4 left-4 right-4 z-50 rounded-xl border border-primary bg-card p-3 text-card-foreground shadow-lg sm:bottom-6 sm:left-auto sm:right-6 sm:min-w-[300px] sm:p-4">
          <div className="mb-3 flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <Clock className={cn('size-5', activeTimer.paused_at ? 'text-muted-foreground' : 'text-primary')} aria-hidden="true" />
              <span className="font-semibold">
                {activeTimer.paused_at ? 'Pausado' : 'Em Andamento'}
              </span>
            </div>
            <div className="font-mono text-2xl font-bold tabular-nums text-primary">
              {formatDuration(elapsedTime)}
            </div>
          </div>

          <dl className="mb-4 flex flex-col gap-2">
            <DetailField label="Serviço:">{activeTimer.service_type_name}</DetailField>
            {activeTimer.client_name && (
              <DetailField label="Cliente:">{activeTimer.client_name}</DetailField>
            )}
            {activeTimer.description && (
              <DetailField label="Descrição:" className="[&_dd]:line-clamp-2">{activeTimer.description}</DetailField>
            )}
          </dl>

          <div className="flex gap-2">
            {activeTimer.paused_at ? (
              <Button className="flex-1" onClick={handleResumeTimer}>
                <Play className="size-4" />
                Retomar
              </Button>
            ) : (
              <Button variant="outline" className="flex-1" onClick={handlePauseTimer}>
                <Pause className="size-4" />
                Pausar
              </Button>
            )}
            <Button variant="destructive" className="flex-1" onClick={handleStopTimer}>
              <Square className="size-4" />
              Parar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
